import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { findProduct, weightSetsCollection } from "../mock";
import { useCart, formatPrice } from "../context/CartContext";
import Header from "../components/Header";
import CartDrawer from "../components/CartDrawer";
import Footer from "../components/Footer";
import StorePresence from "../components/StorePresence";
import Reviews from "../components/Reviews";
import Stars from "../components/Stars";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import {
  ShoppingCart, Minus, Plus, Check, ShieldCheck, Truck, RotateCcw,
  Headphones, Phone, ChevronRight, Star, Zap,
} from "lucide-react";

const trust = [
  { icon: RotateCcw, text: "30-day money back guarantee" },
  { icon: Truck, text: "Fully insured Australia-wide delivery" },
  { icon: ShieldCheck, text: "2 year warranty · lifetime frame guarantee" },
  { icon: Headphones, text: "7-day Australian phone support" },
];

export default function ProductPage() {
  const { slug } = useParams();
  const p = findProduct(slug);
  const { addItem, setOpen } = useCart();
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const discount = p.compareAt ? Math.round((1 - p.price / p.compareAt) * 100) : 0;
  const saved = p.compareAt ? p.compareAt - p.price : 0;
  const related = (p.relatedIds && p.relatedIds.length ? p.relatedIds : [])
    .map((id) => weightSetsCollection.products.find((x) => x.id === id))
    .filter(Boolean);

  const add = (openDrawer = true) => {
    for (let i = 0; i < qty; i++) {
      addItem({ id: p.slug, name: p.name, price: p.price, image: p.images[0] }, false);
    }
    if (openDrawer) setOpen(true);
  };

  return (
    <div className="min-h-screen bg-white font-body">
      <Header />
      <CartDrawer />

      {/* Breadcrumb */}
      <div className="border-b border-navy/10 bg-gray-50">
        <div className="mx-auto flex max-w-7xl items-center gap-1.5 px-4 py-3 text-xs text-navy/50 lg:px-8">
          <Link to="/" className="hover:text-brand">Home</Link>
          <ChevronRight size={12} />
          <Link to="/collection/weight-sets" className="hover:text-brand">Weight Sets</Link>
          <ChevronRight size={12} />
          <span className="text-navy/80">{p.name}</span>
        </div>
      </div>

      {/* Main */}
      <div className="mx-auto max-w-7xl px-4 py-8 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-2">
          {/* Gallery */}
          <div className="flex flex-col-reverse gap-4 md:flex-row">
            {p.images.length > 1 && (
              <div className="flex gap-3 md:flex-col">
                {p.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActive(i)}
                    className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 bg-gray-50 p-1 transition-colors ${
                      active === i ? "border-brand" : "border-navy/10 hover:border-navy/30"
                    }`}
                  >
                    <img src={img} alt="" className="h-full w-full object-contain" />
                  </button>
                ))}
              </div>
            )}
            <div className="relative flex-1 overflow-hidden rounded-2xl border border-navy/10 bg-gray-50 p-6">
              {discount > 0 && (
                <span className="absolute left-4 top-4 z-10 rounded-full bg-brand px-3 py-1 text-xs font-bold text-white">
                  Save {discount}%
                </span>
              )}
              <img src={p.images[active]} alt={p.name} className="h-[420px] w-full object-contain" />
            </div>
          </div>

          {/* Buy box */}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-navy/40">
              {p.brand} · SKU {p.sku}
            </p>
            <h1 className="mt-2 font-heading text-2xl font-extrabold text-navy md:text-3xl">{p.name}</h1>
            <div className="mt-3 flex items-center gap-2">
              <Stars value={p.rating} size={16} />
              <a href="#reviews" className="text-sm text-navy/60 hover:text-brand">
                {p.rating} ({p.reviews} reviews)
              </a>
            </div>

            <div className="mt-5 flex flex-wrap items-baseline gap-3">
              <span className="font-heading text-4xl font-extrabold text-navy">{formatPrice(p.price)}</span>
              {p.compareAt && (
                <span className="text-xl text-navy/40 line-through">{formatPrice(p.compareAt)}</span>
              )}
              {saved > 0 && (
                <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-bold text-green-700">
                  You save {formatPrice(saved)}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-navy/50">or 4 interest-free payments of {formatPrice(p.price / 4)}</p>

            <p className="mt-5 text-sm leading-relaxed text-navy/70">{p.shortDesc}</p>

            <ul className="mt-5 space-y-2">
              {p.highlights.map((h) => (
                <li key={h} className="flex items-start gap-2 text-sm text-navy/80">
                  <Check size={18} className="mt-0.5 shrink-0 text-brand" /> {h}
                </li>
              ))}
            </ul>

            <p className="mt-5 flex items-center gap-2 text-sm font-semibold text-green-600">
              <span className="h-2 w-2 rounded-full bg-green-500" /> In stock — ships within 24 hours
            </p>

            {/* Qty + CTA */}
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <div className="flex items-center rounded-full border border-navy/20">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 text-navy hover:text-brand" aria-label="Decrease"><Minus size={16} /></button>
                <span className="w-10 text-center font-heading font-bold">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="p-3 text-navy hover:text-brand" aria-label="Increase"><Plus size={16} /></button>
              </div>
              <button
                onClick={() => add(true)}
                className="flex flex-1 items-center justify-center gap-2 rounded-full bg-navy px-8 py-3.5 font-heading font-bold text-white transition-colors hover:bg-navy-light"
              >
                <ShoppingCart size={18} /> Add to Cart
              </button>
            </div>
            <button
              onClick={() => add(true)}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-brand px-8 py-3.5 font-heading font-bold text-white transition-colors hover:bg-brand-dark"
            >
              <Zap size={18} /> Buy it Now
            </button>

            <a href="tel:+61296206635" className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold text-navy/70 hover:text-brand">
              <Phone size={16} /> Got a question? Call us on 02 9620 6635
            </a>

            {/* Trust */}
            <div className="mt-6 grid grid-cols-1 gap-3 rounded-2xl border border-navy/10 bg-gray-50 p-4 sm:grid-cols-2">
              {trust.map((t) => (
                <div key={t.text} className="flex items-center gap-2.5">
                  <t.icon size={18} className="shrink-0 text-brand" />
                  <span className="text-xs font-medium text-navy/70">{t.text}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-center gap-2 text-xs font-semibold text-navy/40">
              Secure checkout · Visa · Mastercard · PayPal · Afterpay
            </div>
          </div>
        </div>

        {/* Description & specs */}
        <div className="mt-14 grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-extrabold text-navy">Description</h2>
            <h3 className="mt-4 font-heading text-lg font-bold text-navy">At a Glance</h3>
            {p.atGlance.map((para, i) => (
              <p key={i} className="mt-3 text-sm leading-relaxed text-navy/70">{para}</p>
            ))}
            {p.specs.length > 0 && (
              <>
                <h3 className="mt-6 font-heading text-lg font-bold text-navy">Specifications</h3>
                <div className="mt-3 grid gap-5 sm:grid-cols-2">
                  {p.specs.map((s) => (
                    <div key={s.title} className="rounded-2xl border border-navy/10 p-5">
                      <p className="font-heading text-sm font-bold text-navy">{s.title}</p>
                      <ul className="mt-3 space-y-2">
                        {s.items.map((it) => (
                          <li key={it} className="flex items-start gap-2 text-xs leading-relaxed text-navy/70">
                            <Check size={14} className="mt-0.5 shrink-0 text-brand" /> {it}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* FAQ accordion */}
          <div>
            <h2 className="font-heading text-2xl font-extrabold text-navy">Good to know</h2>
            <Accordion type="single" collapsible className="mt-4">
              {p.faqs.map((f, i) => (
                <AccordionItem key={i} value={`f-${i}`} className="mb-3 overflow-hidden rounded-xl border border-navy/10 bg-gray-50 px-4">
                  <AccordionTrigger className="py-3 text-left font-heading text-sm font-bold text-navy hover:text-brand hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-4 text-xs leading-relaxed text-navy/70">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
        <div className="mt-16">
          <h2 className="mb-6 font-heading text-2xl font-extrabold text-navy">You may also like…</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
            {related.map((r) => {
              const d = Math.round((1 - r.price / r.compareAt) * 100);
              return (
                <Link key={r.id} to={`/product/${r.slug}`} className="group flex flex-col overflow-hidden rounded-2xl border border-navy/10 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
                  <div className="relative aspect-square overflow-hidden bg-gray-50 p-4">
                    <span className="absolute left-3 top-3 rounded-full bg-brand px-2 py-0.5 text-[11px] font-bold text-white">-{d}%</span>
                    <img src={r.image} alt={r.name} className="h-full w-full object-contain transition-transform group-hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col p-4">
                    <h3 className="line-clamp-2 min-h-[40px] text-sm font-semibold text-navy">{r.name}</h3>
                    <div className="mt-2 flex items-baseline gap-2">
                      <span className="font-heading text-lg font-extrabold text-navy">{formatPrice(r.price)}</span>
                      <span className="text-xs text-navy/40 line-through">{formatPrice(r.compareAt)}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
        )}
      </div>

      <StorePresence />
      <Reviews title="What our customers say" subtitle="Verified reviews from Cyberfit customers across Australia." />
      <Footer />
    </div>
  );
}
